import { ChangeDetectionStrategy, Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { FormFieldsModule } from '@shared/components/form-fields/form-fields.module';
import { TsButtonModule, TSIconModule, TsSnackbarService } from 'tsui';
import {
  MergeProjectService,
  PortfolioMerge,
  PortfolioMergePreviewData,
} from '../../@shared/services/merge-project.service';
import { MatLegacyRadioModule } from '@angular/material/legacy-radio';
import GraphqlService from '@services/graphql.service';
import { GraphqlConnection } from '@shared/models/graphql';
import { TsSkeletonComponent } from 'tsui/lib/ts-skeleton/ts-skeleton.component';
import { Pipeline, Project, ProjectTemplate } from '../../projects.types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, catchError, EMPTY, map, noop, Observable, Subject, tap } from 'rxjs';
import { AsyncPipe, JsonPipe } from '@angular/common';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { PusherService } from '@core/services';
import { MatLegacyProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';

@Component({
  selector: 'app-portfolio-merge',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatLegacyFormFieldModule,
    TsButtonModule,
    FormFieldsModule,
    MatLegacySelectModule,
    MatIconModule,
    MatLegacyRadioModule,
    TsSkeletonComponent,
    AsyncPipe,
    MatLegacyInputModule,
    JsonPipe,
    MatLegacyProgressSpinnerModule,
    TSIconModule,
  ],
  templateUrl: './portfolio-merge.component.html',
  styleUrl: './portfolio-merge.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PortfolioMergeComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  form = new FormGroup({
    merge_mode: new FormControl('new'),
    destination_portfolio_id: new FormControl(null, {
      validators: [
        (control: AbstractControl): ValidationErrors | null => {
          if (this.form?.get('merge_mode').value == 'existing' && !control.value) {
            return { required: 'true' };
          }

          return null;
        },
      ],
    }),
    destination_portfolio_name: new FormControl(null, {
      validators: [
        (control: AbstractControl): ValidationErrors | null => {
          if (this.form?.get('merge_mode').value == 'new' && !control.value) {
            return { required: 'true' };
          }

          return null;
        },
      ],
    }),
    destination_portfolio_layout_id: new FormControl(null, {
      validators: [
        (control: AbstractControl): ValidationErrors | null => {
          if (this.form?.get('merge_mode').value == 'new' && !control.value) {
            return { required: 'true' };
          }

          return null;
        },
      ],
    }),
    destination_portfolio_pipeline_id: new FormControl(null, {
      validators: [
        (control: AbstractControl): ValidationErrors | null => {
          if (this.form?.get('merge_mode').value == 'new' && !control.value) {
            return { required: 'true' };
          }

          return null;
        },
      ],
    }),
    child_ids: new FormControl(null),
  });
  ui = new BehaviorSubject({ loading: true, preview: false, merging: false });
  portfolios$: Observable<Pick<Project, 'id' | 'title'>[]>;
  previewData: PortfolioMergePreviewData;
  progressMessages = new BehaviorSubject([]);

  constructor(
    @Inject(MAT_LEGACY_DIALOG_DATA)
    public data: {
      destination_portfolio_pipeline_id: number;
      deals: { id: number; is_parent: boolean; title: string }[];
      templates: ProjectTemplate<any>[];
      pipelines: Pipeline[];
    },
    public matDialogRef: MatLegacyDialogRef<PortfolioMergeComponent>,
    private readonly gqlService: GraphqlService,
    private readonly mergeProjectService: MergeProjectService,
    private readonly pusherService: PusherService,
    private readonly snackBar: TsSnackbarService,
  ) {
    this.form.patchValue({
      destination_portfolio_pipeline_id: data.destination_portfolio_pipeline_id,
      child_ids: data.deals.map(({ id }) => id),
      destination_portfolio_layout_id: data.templates[0].id,
    });
  }

  ngOnInit(): void {
    this.portfolios$ = this.gqlService
      .query<Pick<Project, 'id' | 'title'>>({
        connection: GraphqlConnection.projects,
        fields: ['id', 'title'],
        filter: {
          fjson: `[[\"hierarchy\",\"in\",[\"Portfolio\"]]]`,
          pipeline_id: this.form.get('destination_portfolio_pipeline_id').value,
          pagination: {
            page: 1,
            size: 100,
          },
        },
        options: {
          keepConnectionCase: true,
        },
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((result) => {
          const nodes = result.edges.map(({ node }) => node);
          return nodes.filter(({ id }) => !this.form.get('child_ids').value.includes(id));
        }),
        tap(() => {
          this.ui.next({ ...this.ui.value, loading: false });
        }),
      );

    this.form
      .get('merge_mode')
      .valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((value) => {
          if (value === 'new') {
            this.form.get('destination_portfolio_id').reset();
          } else if (value === 'existing') {
            this.form.get('destination_portfolio_name').reset();
            this.form.get('destination_portfolio_layout_id').reset();
            this.form.get('destination_portfolio_pipeline_id').reset();
          }
        }),
      )
      .subscribe(noop);
  }

  discard(result = false): void {
    this.matDialogRef.close(result);
  }

  createPortfolio(confirmed = false): void {
    const payload: PortfolioMerge = { ...this.form.getRawValue() };
    delete (payload as any).merge_mode;
    payload.confirmed = confirmed;

    this.ui.next({ ...this.ui.value, loading: true });

    this.mergeProjectService
      .portfolioMerge(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((result) => {
          if (!confirmed) {
            this.previewData = result;
            this.ui.next({ ...this.ui.value, preview: true, loading: false });
            this.form.disable();
          } else {
            this.ui.next({ ...this.ui.value, merging: true });
            this.setupPusher(result.id);
          }
        }),
        catchError((error) => {
          this.ui.next({ ...this.ui.value, loading: false });
          let message = `Failed to create portfolio.`;

          if (error.status === 403) {
            message = 'Some of the selected deals cannot be updated';
          }

          this.snackBar.open(message, 'error', null, { duration: 3000 });

          return EMPTY;
        }),
      )
      .subscribe(noop);
  }

  isPortfolio(title: string): boolean {
    return this.data.deals.find((x) => x.title === title)?.is_parent;
  }

  private setupPusher(mergeId: number): void {
    this.pusherService
      .listenToChannel({ channelName: `PortfolioMerge-${mergeId}`, eventName: 'update' })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((data: { status: string }) => {
          this.progressMessages.next([data.status]);
          if (data.status === 'Complete') {
            this.snackBar.open('Portfolio merged successfully', 'info', null, {
              duration: 3000,
            });
            setTimeout(() => {
              this.discard(true);
            }, 1000);
          } else if (data.status === 'An error occurred') {
            this.snackBar.open('An error occurred while merging the portfolio', 'error', null, {
              duration: 3000,
            });
            this.ui.next({ loading: false, preview: true, merging: false });
            this.progressMessages.next([]);
          }
        }),
      )
      .subscribe(noop);
  }
}
